/**
 * Browser-local discussion alert preferences (v1).
 *
 * Future server mirror (same schema in WebOps, Client Portal, and Assistant DBs):
 * user_notification_preference { user_id, discussion_sound_enabled, discussion_toast_enabled,
 * discussion_system_enabled }
 */

export const DISCUSSION_NOTIFICATION_SOUND_KEY = 'jarvis.discussionNotifications.soundEnabled';
export const DISCUSSION_NOTIFICATION_TOAST_KEY = 'jarvis.discussionNotifications.toastEnabled';
export const DISCUSSION_NOTIFICATION_SYSTEM_KEY = 'jarvis.discussionNotifications.systemEnabled';
export const DISCUSSION_NOTIFICATION_PROMPT_DISMISSED_KEY =
  'jarvis.discussionNotifications.promptDismissed';

export const DISCUSSION_NOTIFICATION_PREFS_CHANGED_EVENT =
  'jarvis-discussion-notification-prefs-changed';

export interface DiscussionNotificationPrefs {
  soundEnabled: boolean;
  toastEnabled: boolean;
  systemEnabled: boolean;
  promptDismissed: boolean;
}

function readBoolPref(key: string, defaultValue = false): boolean {
  try {
    const stored = localStorage.getItem(key);
    if (stored === '0' || stored === 'false') return false;
    if (stored === '1' || stored === 'true') return true;
  } catch {
    /* storage unavailable */
  }
  return defaultValue;
}

function writeBoolPref(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function dispatchPrefsChanged(): void {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(DISCUSSION_NOTIFICATION_PREFS_CHANGED_EVENT));
    }
  } catch {
    /* ignore */
  }
}

export function readDiscussionNotificationSoundEnabled(): boolean {
  return readBoolPref(DISCUSSION_NOTIFICATION_SOUND_KEY, false);
}

export function writeDiscussionNotificationSoundEnabled(enabled: boolean): void {
  writeBoolPref(DISCUSSION_NOTIFICATION_SOUND_KEY, enabled);
  dispatchPrefsChanged();
}

export function readDiscussionNotificationToastEnabled(): boolean {
  return readBoolPref(DISCUSSION_NOTIFICATION_TOAST_KEY, false);
}

export function writeDiscussionNotificationToastEnabled(enabled: boolean): void {
  writeBoolPref(DISCUSSION_NOTIFICATION_TOAST_KEY, enabled);
  dispatchPrefsChanged();
}

export function readDiscussionNotificationSystemEnabled(): boolean {
  return readBoolPref(DISCUSSION_NOTIFICATION_SYSTEM_KEY, false);
}

export function writeDiscussionNotificationSystemEnabled(enabled: boolean): void {
  writeBoolPref(DISCUSSION_NOTIFICATION_SYSTEM_KEY, enabled);
  dispatchPrefsChanged();
}

export function readDiscussionNotificationPromptDismissed(): boolean {
  return readBoolPref(DISCUSSION_NOTIFICATION_PROMPT_DISMISSED_KEY, false);
}

export function writeDiscussionNotificationPromptDismissed(dismissed: boolean): void {
  writeBoolPref(DISCUSSION_NOTIFICATION_PROMPT_DISMISSED_KEY, dismissed);
  dispatchPrefsChanged();
}

export function readDiscussionNotificationPrefs(): DiscussionNotificationPrefs {
  return {
    soundEnabled: readDiscussionNotificationSoundEnabled(),
    toastEnabled: readDiscussionNotificationToastEnabled(),
    systemEnabled: readDiscussionNotificationSystemEnabled(),
    promptDismissed: readDiscussionNotificationPromptDismissed()
  };
}

export function writeDiscussionNotificationPrefs(prefs: Partial<DiscussionNotificationPrefs>): void {
  if (prefs.soundEnabled != null) writeBoolPref(DISCUSSION_NOTIFICATION_SOUND_KEY, prefs.soundEnabled);
  if (prefs.toastEnabled != null) writeBoolPref(DISCUSSION_NOTIFICATION_TOAST_KEY, prefs.toastEnabled);
  if (prefs.systemEnabled != null) writeBoolPref(DISCUSSION_NOTIFICATION_SYSTEM_KEY, prefs.systemEnabled);
  if (prefs.promptDismissed != null) {
    writeBoolPref(DISCUSSION_NOTIFICATION_PROMPT_DISMISSED_KEY, prefs.promptDismissed);
  }
  dispatchPrefsChanged();
}

export function hasAnyDiscussionNotificationChannelEnabled(
  prefs: DiscussionNotificationPrefs = readDiscussionNotificationPrefs()
): boolean {
  return prefs.soundEnabled || prefs.toastEnabled || prefs.systemEnabled;
}

export function enableAllDiscussionNotificationChannels(): void {
  writeDiscussionNotificationPrefs({
    soundEnabled: true,
    toastEnabled: true,
    systemEnabled: true
  });
}
